import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { versionService } from '../../services/versionService';

interface VersionHeatmapProps {
  readonly refreshKey?: number;
}

interface CellData {
  date: string;
  versions: string[];
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const CELL_STEP = 12; // 10px celda + 2px gap
const LERP = 0.14;    // factor de suavizado: menor = más elástico

function toLocalISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildGrid(): string[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dayOfWeek = today.getDay();
  const offsetToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const currentWeekMonday = new Date(today);
  currentWeekMonday.setDate(today.getDate() + offsetToMonday);

  const startDate = new Date(currentWeekMonday);
  startDate.setDate(currentWeekMonday.getDate() - 52 * 7);

  const dates: string[] = [];
  const cursor = new Date(startDate);
  const todayStr = toLocalISODate(today);
  while (toLocalISODate(cursor) <= todayStr) {
    dates.push(toLocalISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

function groupByDate(data: { fecha: string; version_code: string }[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const { fecha, version_code } of data) {
    const existing = map.get(fecha);
    if (existing) {
      if (!existing.includes(version_code)) existing.push(version_code);
    } else {
      map.set(fecha, [version_code]);
    }
  }
  return map;
}

function cellColor(count: number): string {
  if (count === 0) return 'bg-gray-100';
  if (count === 1) return 'bg-blue-200';
  if (count === 2) return 'bg-blue-400';
  return 'bg-blue-600';
}

function formatTooltipDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

function buildMonthLabels(grid: string[], weekCount: number): { label: string; col: number }[] {
  const labels: { label: string; col: number }[] = [];
  let lastMonth = -1;

  for (let week = 0; week < weekCount; week++) {
    const dateStr = grid[week * 7];
    if (!dateStr) continue;
    const month = Number.parseInt(dateStr.split('-')[1], 10) - 1;
    if (month !== lastMonth) {
      labels.push({ label: MONTHS[month], col: week });
      lastMonth = month;
    }
  }
  return labels;
}

export function VersionHeatmap({ refreshKey }: VersionHeatmapProps) {
  const [grid] = useState<string[]>(() => buildGrid());
  const [byDate, setByDate] = useState<Map<string, string[]> | null>(null);

  // Tooltip content — solo se actualiza al cambiar de celda (no en cada mousemove)
  const [activeCell, setActiveCell] = useState<CellData | null>(null);
  const [inGrid, setInGrid] = useState(false);

  // Refs para animación de posición — bypasan React para 60fps sin re-renders
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const gridRef = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number>(0);
  const cursorPos = React.useRef({ x: 0, y: 0 });
  const animPos = React.useRef({ x: 0, y: 0 });

  const [totalTooltip, setTotalTooltip] = useState<{ x: number; y: number } | null>(null);

  const weekCount = Math.ceil(grid.length / 7);
  const monthLabels = buildMonthLabels(grid, weekCount);

  useEffect(() => {
    setByDate(null);
    versionService.getVersionDates()
      .then(data => setByDate(groupByDate(data)))
      .catch(() => setByDate(new Map()));
  }, [refreshKey]);

  // Loop RAF: corre mientras el cursor esté dentro del grid
  useEffect(() => {
    if (!inGrid) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    const tick = () => {
      animPos.current.x += (cursorPos.current.x - animPos.current.x) * LERP;
      animPos.current.y += (cursorPos.current.y - animPos.current.y) * LERP;
      const el = tooltipRef.current;
      if (el) {
        const halfW = el.offsetWidth / 2;
        const safeX = Math.max(halfW + 4, Math.min(animPos.current.x, window.innerWidth - halfW - 4));
        el.style.left = `${safeX}px`;
        el.style.top = `${animPos.current.y - 14}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [inGrid]);

  if (byDate === null) {
    return (
      <div className="mb-4">
        <div
          className="grid gap-0.5"
          style={{ gridTemplateColumns: `repeat(${weekCount}, 10px)`, gridTemplateRows: 'repeat(7, 10px)' }}
        >
          {grid.map(date => (
            <div key={date} className="w-[10px] h-[10px] rounded-sm bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  const cells: CellData[] = grid.map(date => ({
    date,
    versions: byDate.get(date) ?? [],
  }));

  const handleGridEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    // Snap inicial para evitar que el tooltip "viaje" desde (0,0)
    cursorPos.current = { x: e.clientX, y: e.clientY };
    animPos.current = { x: e.clientX, y: e.clientY };
    setInGrid(true);
  };

  const handleGridMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    cursorPos.current = { x: e.clientX, y: e.clientY };
    if (!gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const col = Math.max(0, Math.min(weekCount - 1, Math.floor((e.clientX - rect.left) / CELL_STEP)));
    const row = Math.max(0, Math.min(6, Math.floor((e.clientY - rect.top) / CELL_STEP)));
    const cell = cells[col * 7 + row];
    if (cell) setActiveCell(prev => prev?.date === cell.date ? prev : cell);
  };

  const handleGridLeave = () => {
    setInGrid(false);
    setActiveCell(null);
  };

  const totalVersiones = Array.from(byDate.values()).reduce((sum, v) => sum + v.length, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inicioAnio = toLocalISODate(new Date(today.getFullYear(), 0, 1));
  const inicioMes = toLocalISODate(new Date(today.getFullYear(), today.getMonth(), 1));
  const dayOfWeek = today.getDay();
  const offsetLunes = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const inicioSemana = toLocalISODate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + offsetLunes));
  const hoy = toLocalISODate(today);

  const count = (desde: string, hasta?: string) =>
    Array.from(byDate.entries())
      .filter(([date]) => date >= desde && (hasta === undefined || date <= hasta))
      .reduce((sum, [, v]) => sum + v.length, 0);

  const versionesAnio = count(inicioAnio);
  const versionesMes = count(inicioMes);
  const versionesSemana = count(inicioSemana);
  const versionesHoy = count(hoy);

  return (
    <div className="mb-4 py-1 relative select-none">
      <div className="overflow-x-auto overflow-y-hidden" style={{ overscrollBehavior: 'contain' }}>
        <div
          className="flex mb-1"
          style={{ paddingLeft: '16px' }}
        >
          <div
            className="relative"
            style={{ width: `${weekCount * 12}px`, height: '14px' }}
          >
            {monthLabels.map(({ label, col }) => (
              <span
                key={`${label}-${col}`}
                className="absolute text-[10px] text-gray-400 leading-none"
                style={{ left: `${col * 12}px` }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="flex gap-0.5">
          <div className="flex flex-col gap-0.5" style={{ width: '14px' }}>
            {DAYS.map((day, i) => (
              <div key={day} className="h-[10px] flex items-center">
                {i % 2 === 0 && (
                  <span className="text-[9px] text-gray-400 leading-none">{day.slice(0, 1)}</span>
                )}
              </div>
            ))}
          </div>

          <div
            ref={gridRef}
            className="grid gap-0.5"
            style={{
              gridTemplateColumns: `repeat(${weekCount}, 10px)`,
              gridTemplateRows: 'repeat(7, 10px)',
              gridAutoFlow: 'column',
            }}
            onMouseEnter={handleGridEnter}
            onMouseMove={handleGridMouseMove}
            onMouseLeave={handleGridLeave}
          >
            {cells.map((cell, idx) => {
              const count = cell.versions.length;
              const versionesLabel = count === 1 ? '1 versión' : `${count} versiones`;
              const ariaLabel = count > 0
                ? `${formatTooltipDate(cell.date)}: ${versionesLabel}`
                : `${formatTooltipDate(cell.date)}: Sin versiones`;
              const showTooltipForCell = (e: React.FocusEvent) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                cursorPos.current = { x: rect.left + rect.width / 2, y: rect.top };
                animPos.current = { x: rect.left + rect.width / 2, y: rect.top };
                setInGrid(true);
                setActiveCell(cell);
              };
              return (
                <button
                  type="button"
                  key={cell.date}
                  data-idx={idx}
                  aria-label={ariaLabel}
                  className={`w-[10px] h-[10px] rounded-sm border-0 p-0 cursor-default ${cellColor(count)}`}
                  onFocus={showTooltipForCell}
                  onBlur={handleGridLeave}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-2 flex justify-center overflow-x-auto">
        <div className="inline-flex rounded-xl border border-slate-100 bg-slate-50 divide-x divide-slate-100 overflow-hidden shrink-0">
          {[
            { valor: totalVersiones, etiqueta: 'total', hasTooltip: true },
            { valor: versionesAnio, etiqueta: 'año', hasTooltip: false },
            { valor: versionesMes, etiqueta: 'mes', hasTooltip: false },
            { valor: versionesSemana, etiqueta: 'semana', hasTooltip: false },
            { valor: versionesHoy, etiqueta: 'hoy', hasTooltip: false },
          ].map(({ valor, etiqueta, hasTooltip }) => {
            const cellClass = "flex items-baseline justify-center gap-1.5 w-20 py-1.5";
            const content = (
              <>
                <span className="text-sm font-semibold text-slate-500 leading-tight">{valor}</span>
                <span className="text-[10px] text-slate-400 whitespace-nowrap leading-tight flex items-center gap-0.5">
                  {etiqueta}
                  {hasTooltip && <Info className="w-2.5 h-2.5 text-slate-300" />}
                </span>
              </>
            );
            return hasTooltip ? (
              <button
                key={etiqueta}
                type="button"
                className={`${cellClass} border-0 bg-transparent cursor-default`}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTotalTooltip({ x: rect.left + rect.width / 2, y: rect.top });
                }}
                onMouseLeave={() => setTotalTooltip(null)}
              >
                {content}
              </button>
            ) : (
              <div key={etiqueta} className={cellClass}>{content}</div>
            );
          })}
        </div>
      </div>

      {/* Tooltip del heatmap — posición gestionada por RAF, contenido por React */}
      {activeCell && (
        <div
          ref={tooltipRef}
          className="fixed z-[300] pointer-events-none bg-slate-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg max-w-[240px] -translate-x-1/2 -translate-y-full"
          style={{ left: animPos.current.x, top: animPos.current.y - 14 }}
        >
          <div className="font-medium whitespace-nowrap">{formatTooltipDate(activeCell.date)}</div>
          {activeCell.versions.length > 0 ? (
            <div className="text-slate-300 break-words">
              {activeCell.versions.length === 1 ? '1 versión' : `${activeCell.versions.length} versiones`}
              {' · '}{activeCell.versions.map(v => `v${v}`).join(', ')}
            </div>
          ) : (
            <div className="text-slate-400">Sin versiones</div>
          )}
        </div>
      )}

      {totalTooltip && (
        <div
          className="fixed z-[300] pointer-events-none bg-slate-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg -translate-x-1/2 -translate-y-full whitespace-nowrap"
          style={{ left: totalTooltip.x, top: totalTooltip.y - 8 }}
        >
          La primera versión se publicó en noviembre del 2025
        </div>
      )}
    </div>
  );
}
